import { useEffect } from "react";

import { useTheme } from "@/components/ui/theme-provider";

import { useAppName } from "./use-app-name";

// NOTE: En dash, matching the separator used in the tab titles across the app.
const TITLE_SEPARATOR = " – ";

type TDocumentTitleSegments = string | Array<string | null | undefined>;

interface IBuildDocumentTitleProps {
  segments: TDocumentTitleSegments;
  appName: string;
}

// NOTE: Segments are ordered most specific first, so the browser tab stays
// readable when it is narrow and only the leading part survives truncation:
// ["Scheduled Tasks", "Agents"] renders "Scheduled Tasks – Agents – NodeShift AI".
// Empty, nullish and duplicated neighbours are dropped, so a caller can pass a
// parent unconditionally without having to guard it at every call site.
const buildDocumentTitle = ({
  segments,
  appName,
}: IBuildDocumentTitleProps): string => {
  const rawSegments = Array.isArray(segments) ? segments : [segments];
  const parts: string[] = [];

  for (const segment of rawSegments) {
    const trimmed = segment?.trim();
    if (trimmed && trimmed !== parts[parts.length - 1]) {
      parts.push(trimmed);
    }
  }

  if (parts[parts.length - 1] !== appName) {
    parts.push(appName);
  }

  return parts.join(TITLE_SEPARATOR);
};

export const useDocumentTitle = (title?: TDocumentTitleSegments): void => {
  // NOTE: NSAI-6321 — the organization's own platform name when it set one, so
  // the tab of a white-label deployment stops reading "NodeShift AI".
  const appName = useAppName();
  // NOTE: NSAI-6321 — and nothing is written until the branding lookup has
  // answered FOR THE ORGANIZATION ON SCREEN. `useAppName` falls back to the
  // flavor default while that request is in flight, which is the right answer
  // for prose (a sentence cannot wait for a round-trip) but the wrong one here:
  // an early write is what PUTS a name in a white-label customer's tab — the
  // platform's own on first load, and the PREVIOUS customer's after an
  // organization switch, since the resolved name lags the switch by one
  // request. Holding the write costs an untitled tab on first load and a
  // briefly stale one on a switch, and never asserts the wrong brand.
  // `isBrandingResolvedForProject` is already true when no lookup runs at all (branding
  // disabled, the admin shell), so nothing waits on a request that was never
  // made.
  //
  // Holding the write is only half of it, and for a while it was the half that
  // did nothing: the flavor Vite plugin used to inject a <title> into the HTML
  // at build time, so the vendor's name was painted before any of this ran. It
  // no longer does — see `vite-plugins/platform-flavor.ts`. Both halves are
  // needed, and neither works alone.
  const { isBrandingResolvedForProject } = useTheme();
  // NOTE: The effect keys off the resolved string rather than the argument, so
  // passing a fresh array literal on every render does not re-run it.
  const documentTitle = buildDocumentTitle({ segments: title ?? [], appName });

  useEffect(() => {
    if (!isBrandingResolvedForProject) {
      return;
    }

    document.title = documentTitle;
  }, [documentTitle, isBrandingResolvedForProject]);
};
